import React, { useState, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./AgentPanel.css";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

const storeIcon = new L.Icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/862/862856.png', iconSize: [35, 35], iconAnchor: [17, 35], popupAnchor: [0, -35] });
const userIcon = new L.Icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/1946/1946488.png', iconSize: [35, 35], iconAnchor: [17, 35], popupAnchor: [0, -35] });
const agentIcon = new L.Icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/2954/2954884.png', iconSize: [40, 40], iconAnchor: [20, 40], popupAnchor: [0, -40] });

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://backend-ewrn.onrender.com";
const HYDERABAD_FALLBACK = [17.3850, 78.4867];

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const parseCoords = (coords) => {
  if (!coords) return null;

  if (Array.isArray(coords) && coords.length >= 2) {
    const lat = parseFloat(coords[0]);
    const lng = parseFloat(coords[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
    return null;
  }

  if (typeof coords === "string") {
    const parts = coords.split(",");
    if (parts.length < 2) return null;
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
    return null;
  }

  if (typeof coords === "object") {
    const lat = parseFloat(coords.lat);
    const lng = parseFloat(coords.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }

  return null;
};

const normalizeAgent = (agentData) => {
  if (!agentData) return null;
  const normalizedId = agentData.agent_id || agentData.id || "";
  return {
    ...agentData,
    id: normalizedId,
    agent_id: normalizedId,
    name: agentData.name || agentData.agent_name || "",
  };
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("mm-agent-token");
  return {
    "Content-Type": "application/json",
    "Authorization": token ? `Bearer ${token}` : ""
  };
};

export default function AgentPanel() {
  const [agent, setAgent] = useState(() => {
    const saved = localStorage.getItem("mm-agent");
    if (!saved) return null;
    try {
      return normalizeAgent(JSON.parse(saved));
    } catch (err) {
      return null;
    }
  });

  const [isOnline, setIsOnline] = useState(false);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [liveLocation, setLiveLocation] = useState(null);
  const [socket, setSocket] = useState(null);
  const navigate = useNavigate();

  const [agentId, setAgentId] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const [enteredOtp, setEnteredOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [routeCoords, setRouteCoords] = useState([]);
  const [isSimulating, setIsSimulating] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [simIndex, setSimIndex] = useState(0);
  const [workingZone, setWorkingZone] = useState(null);
  const [isZoneSet, setIsZoneSet] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [handoffUrl, setHandoffUrl] = useState("");

  // 1. PERSISTENCE: Fetch active order on load/refresh (Restored perfectly)
  const fetchActiveOrder = useCallback(async () => {
    if (!agent) return;
    try {
      const res = await fetch(`${BASE_URL}/delivery/active`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success && data.orders && data.orders.length > 0) {
        setActiveOrder(data.orders[0]); // Getting the most recent active order
      }
    } catch (err) {
      console.error("Failed to fetch active order:", err);
    }
  }, [agent]);

  useEffect(() => {
    fetchActiveOrder();
  }, [fetchActiveOrder]);

  // 2. SOCKET & UNASSIGNED FEED (Restored Join Room)
  const hasActiveOrder = Boolean(activeOrder);
  useEffect(() => {
    if (!agent) return;

    const newSocket = io(BASE_URL);
    setSocket(newSocket);

    newSocket.emit("agent_connect", { agentId: agent.agent_id || agent.id });

    if (activeOrder) {
      const oid = activeOrder.order_id || activeOrder.id;
      newSocket.emit("join_room", `order_${oid}`);
    }

    newSocket.on("new_unassigned_order", (order) => {
      setUnassignedOrders(prev => {
        const exists = prev.find(o => (o.order_id || o.id) === (order.order_id || order.id));
        if (exists) return prev;
        if (!isZoneSet || !workingZone) return prev;
        const storeCoords = parseCoords(order.store_coords);
        if (!storeCoords) return prev;
        const isWithinZone =
          calculateDistance(workingZone[0], workingZone[1], storeCoords[0], storeCoords[1]) <= 5;
        if (!isWithinZone) return prev;
        return [order, ...prev];
      });
    });

    if (isZoneSet) {
      fetchUnassignedOrders();
    }

    return () => newSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, hasActiveOrder, isZoneSet]); 

  const fetchUnassignedOrders = async (zoneOverride = workingZone) => {
    try {
      const res = await fetch(`${BASE_URL}/delivery/unassigned`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const orders = Array.isArray(data.orders) ? data.orders : [];

        if (!zoneOverride) {
          setUnassignedOrders([]);
          return;
        }

        const [zoneLat, zoneLng] = zoneOverride;
        const filteredOrders = orders.filter((order) => {
          const storeCoords = parseCoords(order.store_coords);
          if (!storeCoords) return false;
          const [storeLat, storeLng] = storeCoords;
          return calculateDistance(zoneLat, zoneLng, storeLat, storeLng) <= 5;
        });

        setUnassignedOrders(filteredOrders);
      }
    } catch (err) {
      console.error("Error fetching unassigned orders:", err);
    }
  };

  // 3. HANDLERS
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await fetch(`${BASE_URL}/delivery/agent/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ agent_id: agentId, password })
      });

      const data = await res.json();
      if (data.success) {
        const normalizedAgent = normalizeAgent(data.agent);
        setAgent(normalizedAgent);
        localStorage.setItem("mm-agent", JSON.stringify(normalizedAgent));
        localStorage.setItem("mm-agent-token", data.token);
        setWorkingZone(null);
        setIsZoneSet(false);
        setUnassignedOrders([]);
      } else {
        setLoginError(data.message || "Invalid Login");
      }
    } catch (err) {
      setLoginError("Server unreachable");
    }
  };

  const handleLogout = () => {
    setAgent(null);
    setActiveOrder(null);
    setWorkingZone(null);
    setIsZoneSet(false);
    setUnassignedOrders([]);
    setShowQRModal(false);
    setHandoffUrl("");
    localStorage.clear(); // Clears all mm-agent data cleanly
  };

  const acceptOrder = async (order) => {
    try {
      const orderId = order.order_id || order.id;
      const res = await fetch(`${BASE_URL}/delivery/accept/${orderId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ agent_id: agent.agent_id || agent.id, agent_name: agent.name })
      });

      const data = await res.json();
      if (data.success) {
        if (socket) socket.emit("join_room", `order_${orderId}`); // Fixed: Emit correctly
        setActiveOrder(data.order);
        setUnassignedOrders(prev => prev.filter(o => (o.order_id || o.id) !== orderId));
      } else {
        alert(data.message || "Could not accept order");
      }
    } catch (err) {
      console.error("Accept failed", err);
    }
  };

  const updateOrderStatus = async (orderId, status, otp = null) => {
    try {
      const res = await fetch(`${BASE_URL}/delivery/status/${orderId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status, otp })
      });
      const data = await res.json();
      if (data.success) {
        setActiveOrder(data.order);
      }
    } catch (err) {
      console.error("Status update error:", err);
    }
  };

  const generateMobileLink = async () => {
    try {
      const res = await fetch(`${BASE_URL}/delivery/agent/handoff`, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      const data = await res.json();

      if (res.ok && data.success && data.handoffToken) {
        const url = `${window.location.origin}/mars-mart/mobile-handoff?token=${data.handoffToken}`;
        setHandoffUrl(url);
        setShowQRModal(true);
        return;
      }

      alert(data.message || "Could not generate mobile handoff link.");
    } catch (err) {
      console.error("Error generating mobile handoff link:", err);
      alert("Could not generate mobile handoff link.");
    }
  };

  const qrModal = showQRModal ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "20px",
          width: "100%",
          maxWidth: "360px",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
          textAlign: "center",
        }}
      >
        <h3 style={{ margin: "0 0 12px 0", fontSize: "18px", color: "#0f172a" }}>Scan to Track on Mobile</h3>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
          <QRCodeSVG value={handoffUrl} size={200} />
        </div>
        <input
          readOnly
          value={handoffUrl}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #cbd5e1",
            fontSize: "12px",
            color: "#334155",
            marginBottom: "12px",
          }}
        />
        <button className="btn-primary w-100" onClick={() => setShowQRModal(false)}>
          Close
        </button>
      </div>
    </div>
  ) : null;

  // Stage-based route rendering for active delivery
  useEffect(() => {
    const fetchRoute = async () => {
      if (!activeOrder) {
        setRouteCoords([]);
        return;
      }

      const orderStatus = activeOrder.status;
      const storeLocation = parseCoords(activeOrder.store_coords) || parseCoords(activeOrder.delivery_agent_coords);
      const customerLoc = parseCoords(activeOrder.customer_coords);

      if (["PENDING", "UNASSIGNED", "DELIVERED"].includes(orderStatus)) {
        setRouteCoords([]);
        return;
      }

      let startLocation = null;
      let endLocation = null;

      if (["ASSIGNED", "AGENT_REACHING_STORE"].includes(orderStatus)) {
        startLocation = liveLocation || workingZone;
        endLocation = storeLocation;
      } else if (["ON_THE_WAY", "REACHED"].includes(orderStatus)) {
        startLocation = liveLocation || storeLocation;
        endLocation = customerLoc;
      } else {
        setRouteCoords([]);
        return;
      }

      if (!startLocation || !endLocation) {
        setRouteCoords([]);
        return;
      }

      try {
        const [startLat, startLng] = startLocation;
        const [endLat, endLng] = endLocation;
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?geometries=geojson`);
        const data = await res.json();
        if (data.routes && data.routes.length > 0) {
          const coords = data.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]);
          setRouteCoords(coords);
        } else {
          setRouteCoords([]);
        }
      } catch (err) {
        console.error("Error fetching stage route", err);
      }
    };

    fetchRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeOrder?.id,
    activeOrder?.order_id,
    activeOrder?.status,
    activeOrder?.store_coords,
    activeOrder?.customer_coords,
    activeOrder?.delivery_agent_coords,
    liveLocation,
    workingZone
  ]);

  const startSimulation = async () => {
    if (!activeOrder || !workingZone) return;

    const storeLocation = parseCoords(activeOrder.store_coords) || parseCoords(activeOrder.delivery_agent_coords);
    const customerLoc = parseCoords(activeOrder.customer_coords);

    if (!storeLocation || !customerLoc) {
      console.error("Missing store/customer coordinates for simulation.");
      return;
    }

    try {
      const [zoneLat, zoneLng] = workingZone;
      const [storeLat, storeLng] = storeLocation;
      const [customerLat, customerLng] = customerLoc;
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${zoneLng},${zoneLat};${storeLng},${storeLat};${customerLng},${customerLat}?geometries=geojson`
      );
      const data = await res.json();

      if (data.routes && data.routes.length > 0) {
        const coords = data.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]);
        setRouteCoords(coords);
        setLiveLocation(coords[0] || workingZone);
      } else {
        setRouteCoords([workingZone, storeLocation, customerLoc]);
        setLiveLocation(workingZone);
      }
    } catch (err) {
      console.error("Error fetching simulation route", err);
      setRouteCoords([workingZone, storeLocation, customerLoc]);
      setLiveLocation(workingZone);
    }

    setSimIndex(0);
    setIsSimulating(true);
  };

  // Simulation Logic
  useEffect(() => {
    let intervalId;
    if (isSimulating && routeCoords.length > 0 && activeOrder) {
      intervalId = setInterval(() => {
        setSimIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= routeCoords.length) {
            setIsSimulating(false);
            clearInterval(intervalId);
            return prev;
          }
          const nextCoord = routeCoords[nextIndex];
          setLiveLocation(nextCoord);
          if (socket) {
            socket.emit("location_update", {
              orderId: activeOrder.order_id || activeOrder.id,
              coords: nextCoord
            });
          }
          return nextIndex;
        });
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSimulating, routeCoords, activeOrder, socket]);

  // 4. GEOLOCATION (Restored exact properties)
  useEffect(() => {
    let watchId;
    if (activeOrder && activeOrder.status === "ON_THE_WAY" && "geolocation" in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const coords = [position.coords.latitude, position.coords.longitude];
          setLiveLocation(coords);
          if (socket) {
            socket.emit("location_update", {
              orderId: activeOrder.order_id || activeOrder.id,
              coords
            });
          }
        },
        (err) => console.error("Geolocation error:", err),
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
      );
    }
    return () => { if (watchId) navigator.geolocation.clearWatch(watchId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrder?.status, socket]);


  // ================= RENDER VIEWS =================

  if (!agent) {
    return (
      <div className="agent-container">
        <div className="agent-login-box">
          {/* Back Button added here */}
      <button className="agent-back-btn" onClick={() => navigate("/mars-mart/home")}>
        ← Back
      </button>
          <h2>MarsMart Agent</h2>
          <form onSubmit={handleLogin}>
            <input type="text" placeholder="DP-1001" value={agentId} onChange={(e) => setAgentId(e.target.value)} required />
            <input type="password" placeholder="123456" value={password} onChange={(e) => setPassword(e.target.value)} required />
            {loginError && <p className="error">{loginError}</p>}
            <button type="submit" className="btn-primary">Login to Fleet</button>
          </form>
        </div>
      </div>
    );
  }

  if (activeOrder) {
    const orderId = activeOrder.order_id || activeOrder.id;
    const { status } = activeOrder;

    return (
      <div className="agent-container">
        <header className="agent-header">
          <div>
            <h3>Order #{orderId}</h3>
            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#475569" }}>
              {agent.name} (ID: {agent.agent_id || agent.id}) | Zone: {isZoneSet ? "Set" : "Not Set"}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              className="btn-secondary"
              onClick={generateMobileLink}
              style={{ border: "none", padding: "8px 12px", borderRadius: "8px", color: "#fff", cursor: "pointer" }}
            >
              📱 Track on Mobile
            </button>
            <span className="badge-status">{status.replace(/_/g, " ")}</span>
          </div>
        </header>

        <div className="active-order-card">
          <p><strong>Store:</strong> {activeOrder.store_name || "MarsMart Hub"}</p>
          <p><strong>Deliver to:</strong> {activeOrder.address_id || "Customer Address"}</p>
          
          <hr />

          {/* Action Buttons based on Status */}
          {(status === "PREPARING" || status === "ASSIGNED") && (
             <div className="action-step">
                <button className="btn-primary w-100" onClick={() => updateOrderStatus(orderId, "AGENT_REACHING_STORE")}>
                  Arrived at Store
                </button>
             </div>
          )}

          {status === "AGENT_REACHING_STORE" && (
             <div className="action-step">
                <button className="btn-secondary w-100" onClick={() => updateOrderStatus(orderId, "PICKED_UP")}>
                  Confirm Pickup
                </button>
             </div>
          )}

          {status === "PICKED_UP" && (
             <div className="action-step">
               <div className="items-list" style={{background: "#f1f5f9", padding: "1rem", borderRadius: "8px", marginBottom: "15px"}}>
                 <h5 style={{margin: "0 0 10px 0"}}>Items to Deliver:</h5>
                 <ul style={{ margin: 0, paddingLeft: "20px" }}>
                   {(() => {
                     try {
                        const itemsArr = typeof activeOrder.items === 'string' ? JSON.parse(activeOrder.items) : activeOrder.items;
                        const cleanItems = itemsArr.filter(i => !i._isSecretLocation); 
                        return cleanItems.map((item, idx) => (
                          <li key={idx} style={{ marginBottom: "5px" }}>
                            {item.quantity}x {item.name} - ₹{item.price * item.quantity}
                          </li>
                        ));
                     } catch(e) { return <li>No items found</li>; }
                   })()}
                 </ul>
               </div>
               <button className="btn-primary w-100 mt-3" onClick={() => updateOrderStatus(orderId, "ON_THE_WAY")}>
                 Start Navigation
               </button>
             </div>
          )}

          {["ASSIGNED", "AGENT_REACHING_STORE", "ON_THE_WAY"].includes(status) && (
            <div className="map-wrapper">
                {(() => {
                  const storeLocation =
                    parseCoords(activeOrder?.store_coords) ||
                    parseCoords(activeOrder?.delivery_agent_coords) ||
                    HYDERABAD_FALLBACK;
                  const customerLoc = parseCoords(activeOrder?.customer_coords) || HYDERABAD_FALLBACK;
                  const isStageOne = ["ASSIGNED", "AGENT_REACHING_STORE"].includes(status);
                  const currentPoint = isStageOne
                    ? (liveLocation || workingZone || storeLocation || HYDERABAD_FALLBACK)
                    : (liveLocation || storeLocation || workingZone || HYDERABAD_FALLBACK);
                  const targetLocation = isStageOne ? storeLocation : customerLoc;
                  const distanceLabel = isStageOne ? "Store" : "Customer";
                  const canSimulate =
                    Boolean(workingZone) &&
                    Boolean(parseCoords(activeOrder?.store_coords) || parseCoords(activeOrder?.delivery_agent_coords)) &&
                    Boolean(parseCoords(activeOrder?.customer_coords));

                  return (
                    <>
                      <MapContainer center={currentPoint} zoom={13} style={{ height: "250px", width: "100%", borderRadius: "8px" }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        {liveLocation && <Marker position={liveLocation} icon={agentIcon}><Popup>You (Agent)</Popup></Marker>}
                        {workingZone && <Marker position={workingZone} icon={agentIcon}><Popup>Working Zone</Popup></Marker>}
                        <Marker position={storeLocation} icon={storeIcon}><Popup>Store</Popup></Marker>
                        <Marker position={customerLoc} icon={userIcon}><Popup>Customer Destination</Popup></Marker>
                        {routeCoords && routeCoords.length > 0 && (
                          <Polyline positions={routeCoords} color={isStageOne ? "orange" : "blue"} weight={5} opacity={0.7} />
                        )}
                      </MapContainer>
                      
                      <div className="mt-3 p-2" style={{ background: "#f8fafc", borderRadius: "8px", border: "1px solid #cbd5e1", textAlign: "center" }}>
                        <p style={{ margin: 0, fontWeight: "bold", color: "#334155" }}>
                          Distance to {distanceLabel}: <span style={{ color: "#059669" }}>{
                            calculateDistance(
                              targetLocation[0],
                              targetLocation[1],
                              currentPoint[0],
                              currentPoint[1]
                            ).toFixed(1)
                          } km</span>
                        </p>
                        {status === "ON_THE_WAY" && (
                          <button 
                            className="btn-secondary w-100 mt-2" 
                            onClick={startSimulation}
                            disabled={isSimulating || !canSimulate}
                            style={{ background: isSimulating || !canSimulate ? "#cbd5e1" : "#3b82f6", color: "#fff", border: "none", padding: "10px", borderRadius: "6px", cursor: isSimulating || !canSimulate ? "not-allowed" : "pointer", fontWeight: "bold" }}
                          >
                            {isSimulating ? "Simulating Live Tracking..." : "Start Live Simulation"}
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
            </div>
          )}

          {status === "ON_THE_WAY" && (
            <button
              className="btn-primary mt-4 w-100"
              style={{ backgroundColor: "#059669" }}
              onClick={() => updateOrderStatus(orderId, "REACHED")}
            >
                📍 Reached Customer Location
              </button>
          )}

          {status === "REACHED" && (
            <div className="action-step">
               <div className="mt-2 p-3" style={{ background: "#fef0e9", borderRadius: "8px", border: "1px solid #ed5d1a" }}>
                  <h4 style={{ margin: "0 0 10px 0", color: "#ed5d1a" }}>Verify Delivery</h4>
                  <p style={{ fontSize: "14px", margin: "0 0 10px 0" }}>Agent arrived! Ask the customer for their 4-digit OTP.</p>
                  <input 
                    type="text" 
                    placeholder="Enter 4-digit OTP" 
                    value={enteredOtp} 
                    onChange={(e) => setEnteredOtp(e.target.value)}
                    maxLength={4}
                    style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc", marginBottom: "10px", textAlign: "center", fontSize: "18px", letterSpacing: "4px" }}
                  />
                  {otpError && <p style={{ color: "red", fontSize: "12px", margin: "0 0 10px 0" }}>{otpError}</p>}
                  <button 
                    className="btn-primary w-100"
                    onClick={() => {
                      const expectedOtp = String((activeOrder.order_id || activeOrder.id) * 73 + 1000).substring(0, 4);
                      if(enteredOtp === expectedOtp) {
                        setOtpError("");
                        updateOrderStatus(orderId, "DELIVERED", enteredOtp);
                      } else {
                        setOtpError("Invalid OTP. Please try again.");
                      }
                    }}
                  >
                    Verify & Complete Delivery
                  </button>
               </div>
            </div>
          )}

          {status === "DELIVERED" && (
            <div className="success-area mt-4 text-center">
              <h4 style={{ color: "#059669", fontSize: "20px", marginBottom: "15px" }}>Delivery Completed! 🎉</h4>
              <button className="btn-primary w-100" onClick={() => setActiveOrder(null)}>Return to Dashboard</button>
            </div>
          )}
        </div>
        {qrModal}
      </div>
    );
  }

  if (!isZoneSet) {
    const zoneCenter = workingZone || HYDERABAD_FALLBACK;
    return (
      <div className="agent-container">
        <header className="agent-header">
          <div className="agent-profile-section">
            <h2>{agent.name}</h2>
            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#475569" }}>
              ID: {agent.agent_id || agent.id} | Zone: Not Set
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              className="btn-secondary"
              onClick={generateMobileLink}
              style={{ border: "none", padding: "10px 12px", borderRadius: "8px", color: "#fff", cursor: "pointer" }}
            >
              📱 Track on Mobile
            </button>
            <button className="btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </header>

        <main className="dashboard-content">
          <div className="section-header">
            <h3>Set Working Zone</h3>
          </div>
          <div className="order-card" style={{ display: "block" }}>
            <p style={{ marginBottom: "12px", color: "#334155" }}>
              Drag the marker to confirm your 5km working zone center.
            </p>
            <MapContainer center={zoneCenter} zoom={13} style={{ height: "300px", width: "100%", borderRadius: "8px" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker
                position={zoneCenter}
                icon={agentIcon}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    setWorkingZone([lat, lng]);
                  },
                }}
              >
                <Popup>Set your working zone</Popup>
              </Marker>
            </MapContainer>
            <button
              className="btn-primary w-100 mt-3"
              onClick={async () => {
                const confirmedZone = workingZone || HYDERABAD_FALLBACK;
                setWorkingZone(confirmedZone);
                setIsZoneSet(true);
                await fetchUnassignedOrders(confirmedZone);
              }}
            >
              Confirm Work Zone
            </button>
          </div>
        </main>
        {qrModal}
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="agent-container">
     <header className="agent-header">
      <div className="agent-profile-section">
        <h2>{agent.name}</h2>
        <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#475569" }}>
          ID: {agent.agent_id || agent.id} | Zone: {isZoneSet ? "Set" : "Not Set"}
        </p>
        {/* NEW ENHANCED TOGGLE */}
        <div className="status-toggle-wrapper">
          <span className={`status-indicator-text ${isOnline ? "online" : "offline"}`}>
            {isOnline ? "ACTIVE & ONLINE" : "CURRENTLY OFFLINE"}
          </span>
          <label className="main-status-switch">
            <input 
              type="checkbox" 
              checked={isOnline} 
              onChange={() => setIsOnline(!isOnline)} 
            />
            <span className="main-status-slider">
              <span className="slider-icon">{isOnline ? "🟢" : "🔴"}</span>
            </span>
          </label>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          className="btn-secondary"
          onClick={generateMobileLink}
          style={{ border: "none", padding: "10px 12px", borderRadius: "8px", color: "#fff", cursor: "pointer" }}
        >
          📱 Track on Mobile
        </button>
        <button className="btn-logout" onClick={handleLogout}>Logout</button>
      </div>
    </header>

      <main className="dashboard-content">
        <div className="section-header">
          <h3>Available Jobs</h3>
          <button className="btn-refresh" onClick={fetchUnassignedOrders}>↻</button>
        </div>

        {!isOnline ? (
          <div className="empty-state">Go online to start receiving orders.</div>
        ) : unassignedOrders.length === 0 ? (
          <div className="empty-state">Searching for nearby orders...</div>
        ) : (
          <div className="orders-list">
            {unassignedOrders.map(order => (
              <div key={order.order_id || order.id} className="order-card">
                <div className="order-main">
                  <h4>Order #{order.order_id || order.id}</h4>
                  <p>{order.items ? (typeof order.items === 'string' ? JSON.parse(order.items).length : order.items.length) : 0} Items</p>
                  <p className="store-name">{order.store_name || "MarsMart Hub"}</p>
                </div>
                <button className="btn-accept" onClick={() => acceptOrder(order)}>Accept</button>
              </div>
            ))}
          </div>
        )}
      </main>
      {qrModal}
    </div>
  );
}
